import React, { useEffect, useState } from 'react';

import { App, Button, Input, Row, Skeleton, Space, TableColumnsType, Tooltip } from 'antd';
import Link from 'antd/es/typography/Link';

import { DeleteOutlined, EditOutlined, QrcodeOutlined } from '@ant-design/icons';
import List from 'components/_Common/List';
import { ClientCreateModal } from 'components/Account/Client/ClientCreateModal';
import { ClientEditModal } from 'components/Account/Client/ClientEditModal';
import { ClientQrCodeModal } from 'components/Account/Client/ClientQrCodeModal';
import Authentication from 'lib/Authentication';
import CobreFacil from 'lib/CobreFacil';
import { clientURL } from 'lib/helpers/Contratc.helper';
import { makeCnpj } from 'lib/helpers/Domain.helper';
import { useAccount } from 'lib/providers/AccountContextProvider';
import { Show } from 'lib/Show';
import { deleteAccountClient } from 'services/Account.service';

export function SettingsClientManager() {
    const {
        clients,
        isCreateClientModalVisible,
        isEditClientModalVisible,
        isQrCodeModalVisible,
        setIsCreateClientModalVisible,
        setIsEditClientModalVisible,
        setIsQrCodeModalVisible,
        setClientId,
        subscription,
        clientManager,
        fetchClients,
        filteredClients,
        search,
        isLoading,
        setSearch
    } = useAccount();

    const app = App.useApp();
    const [localLoading, setLocalLoading] = useState(true);


    useEffect(() => {
        if (subscription || (!isLoading && subscription === null)) {
            setLocalLoading(false);
        }
    }, [isLoading, subscription]);

    const handleEdit = (clientId: Client.Model['id']) => {
        setClientId(clientId);
        setIsEditClientModalVisible(true);
    };

    const handleQrCode = (clientId: Client.Model['id']) => {
        setClientId(clientId);
        setIsQrCodeModalVisible(true);
    };

    const handleDelete = async (clientId: Client.Model['id']) => {
        const response = await deleteAccountClient(clientId);

        if (!response.success)
            return app.notification.error({ message: response.message });

        const { message, description } = response;
        app.notification.success({ message, description });

        fetchClients();
    };

    const COLUMNS: TableColumnsType<Client.Model> = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'CNPJ',
            dataIndex: 'cnpj',
            key: 'cnpj',
            render: (value) => (value ? makeCnpj(value) : '-'),
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            render: (value) =>
                value ? (
                    <Link href={`${clientURL()}/${value}`} target="_blank">
                        {clientURL()}/{value}
                    </Link>
                ) : (
                    '-'
                ),
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            align: 'center',
            render: (_, client) =>
                Authentication.getClients().find((c) => c.id === client.id) && (
                    <Space>
                        <Show when={client.url !== null && client.url !== ''}>
                            <Button
                                type="text"
                                icon={<QrcodeOutlined />}
                                onClick={() => handleQrCode(client.id)}
                                title="QR Code"
                            />
                        </Show>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(client.id)}
                            title="Editar"
                        />
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() =>
                                app.modal.confirm({
                                    title: 'Excluir condomínio',
                                    content: 'Tem certeza que deseja excluir este condomínio?',
                                    okText: 'Sim',
                                    okType: 'danger',
                                    onOk: async () => await handleDelete(client.id),
                                    cancelText: 'Não',
                                })
                            }
                            title="Excluir"
                        />
                    </Space>
                ),
        },
    ];


    const isLimitReaching = (): boolean => {
        const isImplantacao = !clientManager.cobre_facil_id;

        if (isImplantacao) return false; 

        if (subscription?.plans_id) {
            const planObj = CobreFacil.findPlan(subscription.plans_id);

            if (planObj) return clients.length >= planObj.limit;
        }

        return true; 
    };
    return (
        <>
            <Show when={localLoading}>
                <Skeleton active paragraph={{ rows: 4 }} />
            </Show>

            <Show when={!localLoading}>
                <Row justify="space-between" align="middle">
                    <Input.Search
                        placeholder="Pesquisar"
                        allowClear
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ maxWidth: 400 }}
                    />
                    <Tooltip title={isLimitReaching() ? 'Limite de condomínios atingido. Entre em contato pelo suporte para cadastrar novos condomínios.' : ''}>
                        <Button
                            disabled={isLimitReaching()}
                            type="primary"
                            onClick={() => setIsCreateClientModalVisible(true)}
                        >
                            Cadastrar
                        </Button>
                    </Tooltip>
                </Row>

                <List columns={COLUMNS} data={filteredClients} />

                <Show when={isCreateClientModalVisible}>
                    <ClientCreateModal />
                </Show>

                <Show when={isEditClientModalVisible}>
                    <ClientEditModal />
                </Show>

                <Show when={isQrCodeModalVisible}>
                    <ClientQrCodeModal />
                </Show>
            </Show>
        </>
    );
}